import { useListAddonInstancesInfinite } from 'api/frontend-infinite';
import { useMemo } from 'react';
import { ADDONS } from 'src/data/version_register_addons';
import { flattenPages } from 'utils/arrays';
import { useClusterIdToNameMap } from './useClusterIdToNameMap';

/** Per addon instance: its cluster id and resolved cluster display name. */
export type AddonInstanceClusterMapEntry = {
  clusterId: string;
  clusterName: string;
};

export function addonInstanceClusterDisplayLabel(
  entry: AddonInstanceClusterMapEntry | undefined,
  addonInstanceId: string,
): string {
  if (!entry) return addonInstanceId;
  const name = entry.clusterName.trim();
  return name || entry.clusterId || addonInstanceId;
}

export function useAddonInstanceToClusterMap({
  token,
  useExampleData,
}: {
  token: string;
  useExampleData: boolean;
}): Map<string, AddonInstanceClusterMapEntry> {
  const clusterNameMap = useClusterIdToNameMap({
    token,
    useExampleData,
  });

  const { data: addonInstancePages } = useListAddonInstancesInfinite(
    {},
    { request: { headers: { Authorization: `Bearer ${token}` } } },
  );
  const addonInstances = flattenPages(addonInstancePages);

  return useMemo(() => {
    const row = (clusterId: string): AddonInstanceClusterMapEntry => ({
      clusterId: clusterId,
      clusterName: clusterNameMap.get(clusterId) ?? clusterId,
    });

    if (useExampleData) {
      return new Map(ADDONS.map((a) => [a.id, row(a.cluster_id)] as const));
    }
    return new Map(
      (addonInstances ?? []).map((a) => [a.id, row(a.cluster_id)] as const),
    );
  }, [useExampleData, clusterNameMap, addonInstances]);
}
